import { useState } from 'react'
import { useArrowKeyNav, useClickOutside } from '@nv/hooks'
import ChevronIcon from '@nv/images/design/chevron-icon'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { FormikError } from '../formik_error/FormikError'
import { NVButton } from '../nv_button/NVButton'
import { Times } from './presets/Times'
import styles from './Select.module.scss'

export function Select({
  label,
  formik,
  options,
  field,
  value,
  isRequired = false,
  onChange,
  classes = {},
  style = {},
  ariaLabel,
  isSearchable = false,
  disabled = false,
}: any) {
  const { t } = useTranslation()
  if (options === 'times') options = Times

  const [searchTerm, setSearchTerm] = useState('')
  const [open, setOpen] = useState(false)
  const openDropdown = () => !disabled && setOpen(true)
  const closeDropdown = () => setOpen(false)

  const filteredOptions = isSearchable
    ? options.filter((item) => (item.label || item.id).toLowerCase().includes(searchTerm.toLowerCase()))
    : options

  const selectedLabel = value ? options.find((option) => option.value === value)?.label || value.id : null
  const placeholder = selectedLabel || t('form.select.selectOption')

  const handleSelectOption = (item) => {
    if (disabled) return
    formik?.setFieldValue(field, item.value)
    onChange?.(item)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const { handleContainerKeyDown, handleOptionsKeyDown, optionRefs, buttonRef, containerRef, searchInputRef } = useArrowKeyNav({
    options: filteredOptions,
    onFocus: openDropdown,
    onBlur: closeDropdown,
    activation: 'enter',
    onCallback: handleSelectOption,
  })

  useClickOutside(containerRef, closeDropdown)

  return (
    <div
      ref={containerRef}
      style={{ ...style.container }}
      className={clsx(styles['container'], classes.container, {
        [styles['disabled']]: disabled,
      })}
    >
      <label className={styles['label']}>
        {label} {isRequired && <span className={styles['required']}>*</span>}
      </label>

      <div
        className={clsx(
          styles['select'],
          {
            [styles['error-select']]: formik?.touched[field] && formik?.errors[field],
            [styles['disabled']]: disabled,
          },
          classes.select,
        )}
        role='combobox'
        onKeyDown={handleContainerKeyDown}
        onClick={openDropdown}
        tabIndex={disabled ? -1 : 0}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls={`${field}-listbox`}
        aria-label={ariaLabel || 'Select input'}
        ref={buttonRef}
      >
        <span className={clsx({ [styles['placeholder']]: !selectedLabel })}>{placeholder}</span>
        <ChevronIcon
          className={clsx({
            [styles['chevron-flip']]: open,
            [styles['chevron']]: !open,
          })}
        />
      </div>

      {open && (
        <div id={`${field}-listbox`} style={{ ...style.content }} className={clsx(styles['options'], classes.content)} role='listbox'>
          {isSearchable && (
            <input
              ref={searchInputRef}
              onKeyDown={handleOptionsKeyDown}
              className={styles['search']}
              placeholder={t('main.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label='Search options'
            />
          )}
          {filteredOptions.map((item, index) => (
            <NVButton
              key={index}
              variant='text'
              stretchContent={true}
              text={item.label || item.id}
              classes={{ container: styles['option'] }}
              ref={(el) => (optionRefs.current[index] = el)}
              onClick={() => handleSelectOption(item)}
              onKeyDown={handleOptionsKeyDown}
              role='option'
              tabIndex={-1}
            />
          ))}
        </div>
      )}

      <FormikError formik={formik} fieldName={field} />
    </div>
  )
}
